import React from "react";

/**
 * CylinderBlueprint — a spec-driven navy/white blueprint watermark.
 * Renders a proportional hydraulic-cylinder line-drawing from real dimensions.
 *
 * Props
 *   bore       — bore diameter in mm (number)
 *   rod        — rod diameter in mm (number)
 *   stroke     — stroke length in mm (number)
 *   cylinderNumber, drawingNumber, customerName, plantName — optional labels
 *   variant    — "watermark" (default, faint) or "certificate" (bold, printable)
 */
export default function CylinderBlueprint({
  bore = 100,
  rod = 56,
  stroke = 500,
  cylinderNumber,
  drawingNumber,
  customerName,
  plantName,
  variant = "watermark",
  className = "",
  "data-testid": testId = "cylinder-blueprint",
}) {
  // Canvas geometry — normalize the drawing to a 900×360 viewBox
  const canvasW = 900;
  const canvasH = 360;
  const marginX = 90;
  const marginY = 90;
  const drawW = canvasW - 2 * marginX;
  const drawH = canvasH - 2 * marginY;

  // Scale factors — cylinder body length is proportional to stroke, height to bore
  // Clamp so extreme aspect ratios don't overflow the canvas.
  const bodyLen = Math.min(drawW * 0.6, Math.max(drawW * 0.25, stroke * 0.75));
  const bodyH = Math.min(drawH * 0.55, Math.max(drawH * 0.2, bore * 1.4));
  const rodH = Math.max(6, Math.min(bodyH * 0.35, rod * 1.4));

  // Positions
  const cx = canvasW / 2;
  const cy = canvasH / 2;
  const bodyX = cx - bodyLen / 2;
  const bodyY = cy - bodyH / 2;
  const capW = 26;
  const rodLen = Math.min(drawW * 0.28, bodyLen * 0.55);

  const stroke1 = variant === "watermark" ? "white" : "#0B1F3A";
  const bgFill = variant === "watermark" ? "transparent" : "#F7F8FA";
  const opacity = variant === "watermark" ? 0.09 : 1;
  const labelFill = variant === "watermark" ? "white" : "#0B1F3A";

  return (
    <svg
      viewBox={`0 0 ${canvasW} ${canvasH}`}
      className={className}
      preserveAspectRatio="xMidYMid meet"
      data-testid={testId}
    >
      {/* Grid backdrop */}
      <defs>
        <pattern id={`grid-${testId}`} width="30" height="30" patternUnits="userSpaceOnUse">
          <path d="M 30 0 L 0 0 0 30" fill="none" stroke={stroke1} strokeWidth="0.4" />
        </pattern>
      </defs>
      <rect width={canvasW} height={canvasH} fill={bgFill} />
      <g opacity={opacity}>
        <rect width={canvasW} height={canvasH} fill={`url(#grid-${testId})`} />

        {/* Cylinder body */}
        <g stroke={stroke1} strokeWidth={variant === "certificate" ? 1.6 : 1.2} fill="none" strokeLinejoin="round">
          {/* Outer barrel */}
          <rect x={bodyX} y={bodyY} width={bodyLen} height={bodyH} rx="6" />
          <rect x={bodyX + 10} y={bodyY + 8} width={bodyLen - 20} height={bodyH - 16} />

          {/* End caps */}
          <rect x={bodyX - capW} y={bodyY - 8} width={capW} height={bodyH + 16} rx="4" />
          <rect x={bodyX + bodyLen} y={bodyY - 8} width={capW} height={bodyH + 16} rx="4" />

          {/* Piston rod protruding right */}
          <line
            x1={bodyX + bodyLen + capW}
            y1={cy}
            x2={bodyX + bodyLen + capW + rodLen}
            y2={cy}
            strokeWidth={Math.max(4, rodH / 2)}
          />
          {/* Rod eye */}
          <circle cx={bodyX + bodyLen + capW + rodLen} cy={cy} r={Math.max(9, rodH / 2 + 3)} />
          <circle cx={bodyX + bodyLen + capW + rodLen} cy={cy} r="3" fill={stroke1} />

          {/* Left mounting */}
          <circle cx={bodyX - capW - 12} cy={cy} r="10" />
          <circle cx={bodyX - capW - 12} cy={cy} r="3" fill={stroke1} />

          {/* Ports */}
          <line x1={bodyX + bodyLen * 0.15} y1={bodyY} x2={bodyX + bodyLen * 0.15} y2={bodyY - 26} />
          <rect x={bodyX + bodyLen * 0.15 - 10} y={bodyY - 40} width="20" height="14" />
          <line x1={bodyX + bodyLen * 0.85} y1={bodyY} x2={bodyX + bodyLen * 0.85} y2={bodyY - 26} />
          <rect x={bodyX + bodyLen * 0.85 - 10} y={bodyY - 40} width="20" height="14" />

          {/* STROKE dimension line below */}
          <line
            x1={bodyX} y1={bodyY + bodyH + 34} x2={bodyX + bodyLen} y2={bodyY + bodyH + 34}
            strokeDasharray="2 3"
          />
          <line x1={bodyX} y1={bodyY + bodyH + 29} x2={bodyX} y2={bodyY + bodyH + 39} />
          <line x1={bodyX + bodyLen} y1={bodyY + bodyH + 29} x2={bodyX + bodyLen} y2={bodyY + bodyH + 39} />

          {/* BORE dimension line on left */}
          <line
            x1={bodyX - capW - 34} y1={bodyY} x2={bodyX - capW - 34} y2={bodyY + bodyH}
            strokeDasharray="2 3"
          />
          <line x1={bodyX - capW - 39} y1={bodyY} x2={bodyX - capW - 29} y2={bodyY} />
          <line x1={bodyX - capW - 39} y1={bodyY + bodyH} x2={bodyX - capW - 29} y2={bodyY + bodyH} />

          {/* ROD Ø dimension callout */}
          <line
            x1={bodyX + bodyLen + capW + 10}
            y1={cy - rodH / 2}
            x2={bodyX + bodyLen + capW + 10}
            y2={cy + rodH / 2}
            strokeDasharray="2 3"
          />
        </g>

        {/* Dimension labels */}
        <g fontFamily="Inter, sans-serif" fill={labelFill}>
          <text x={cx} y={bodyY + bodyH + 55} fontSize="12" textAnchor="middle" letterSpacing="4">
            STROKE Ø {Number(stroke).toLocaleString()} mm
          </text>
          <text
            x={bodyX - capW - 50}
            y={cy}
            fontSize="12"
            textAnchor="middle"
            letterSpacing="4"
            transform={`rotate(-90 ${bodyX - capW - 50} ${cy})`}
          >
            BORE Ø {Number(bore).toLocaleString()} mm
          </text>
          <text
            x={bodyX + bodyLen + capW + rodLen / 2}
            y={cy - rodH / 2 - 8}
            fontSize="11"
            textAnchor="middle"
            letterSpacing="3"
          >
            ROD Ø {Number(rod).toLocaleString()}
          </text>
        </g>
      </g>

      {/* Overlay labels for certificate mode (always fully opaque) */}
      {variant === "certificate" && (
        <g fontFamily="Inter, sans-serif" fill="#0B1F3A">
          <text x="30" y="34" fontSize="18" fontWeight="700" letterSpacing="1">
            HISPL NEXUS · Cylinder Digital Passport
          </text>
          {cylinderNumber && (
            <text x="30" y="56" fontSize="12" fill="#C62828" letterSpacing="2" fontWeight="600">
              CYLINDER #{cylinderNumber}
            </text>
          )}
          {drawingNumber && (
            <text x={canvasW - 30} y="34" fontSize="12" textAnchor="end" fontFamily="IBM Plex Mono, monospace">
              DWG {drawingNumber}
            </text>
          )}
          {customerName && (
            <text x={canvasW - 30} y="56" fontSize="11" textAnchor="end" fill="#6B7280">
              {customerName}{plantName ? ` · ${plantName}` : ""}
            </text>
          )}
          <text x={canvasW - 30} y={canvasH - 20} fontSize="10" textAnchor="end" fill="#6B7280" letterSpacing="2">
            HYDRAULICS INDIA SERVICES PVT LTD
          </text>
        </g>
      )}
    </svg>
  );
}
